// src/pages/Appli/AppliAdmin/VisitorCenter/hooks/useVisitorSessions.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getVisitorSessionById,
  getVisitorSessions,
} from "./../../../../../services/visitorTrackingServices";

const INITIAL_FILTERS = {
  path: "",
  source: "",
  country_code: "",
  status: "",
  device_type: "",
  date_from: "",
  date_to: "",
  exclude_bots: "true",
};

export default function useVisitorSessions({
  enabled = false,
} = {}) {
  const [rows, setRows] = useState([]);

  const [filters, setFilters] =
    useState(INITIAL_FILTERS);

  const [debouncedFilters, setDebouncedFilters] =
    useState(INITIAL_FILTERS);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [totalPages, setTotalPages] =
    useState(1);

  const [count, setCount] =
    useState(0);

  const [
    selectedSession,
    setSelectedSession,
  ] = useState(null);

  const [loading, setLoading] =
    useState(false);

  const [
    detailsLoading,
    setDetailsLoading,
  ] = useState(false);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  /**
   * Évite de lancer une requête à chaque caractère
   * saisi dans les champs de recherche.
   */
  useEffect(() => {
    const timeoutId =
      window.setTimeout(() => {
        setDebouncedFilters(filters);
      }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [filters]);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
        }

        setErrorMessage("");

        const response =
          await getVisitorSessions({
            page,
            limit,
            ...debouncedFilters,
          });

        const nextRows =
          response?.data || [];

        const nextCount =
          Number(response?.count) || 0;

        const nextTotalPages =
          Math.max(
            Number(
              response?.totalPages,
            ) || 1,
            1,
          );

        setRows(nextRows);
        setCount(nextCount);
        setTotalPages(
          nextTotalPages,
        );

        /*
         * Si le serveur indique que la page actuelle
         * n'existe plus, on revient à la page 1.
         */
        if (
          page > nextTotalPages &&
          nextTotalPages >= 1
        ) {
          setPage(1);
        }

        /*
         * On conserve le détail ouvert uniquement
         * si la session existe toujours dans la liste.
         */
        setSelectedSession(
          (currentSession) => {
            if (!currentSession) {
              return null;
            }

            const stillVisible =
              nextRows.some(
                (row) =>
                  Number(row.id) ===
                  Number(
                    currentSession.id,
                  ),
              );

            return stillVisible
              ? currentSession
              : null;
          },
        );
      } catch (error) {
        setErrorMessage(
          error?.response?.data
            ?.error ||
            error?.response?.data
              ?.message ||
            "Impossible de récupérer l’historique des sessions.",
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [
      debouncedFilters,
      limit,
      page,
    ],
  );

  const updateFilter =
    useCallback((name, value) => {
      setFilters((current) => ({
        ...current,
        [name]: value,
      }));

      setPage(1);
    }, []);

  const resetFilters =
    useCallback(() => {
      setFilters(
        INITIAL_FILTERS,
      );

      setDebouncedFilters(
        INITIAL_FILTERS,
      );

      setPage(1);
      setSelectedSession(null);
    }, []);

  const selectSession =
    useCallback(
      async (sessionId) => {
        if (!sessionId) {
          setSelectedSession(null);
          return;
        }

        try {
          setDetailsLoading(true);
          setErrorMessage("");

          const response =
            await getVisitorSessionById(
              sessionId,
            );

          setSelectedSession(
            response?.data || null,
          );
        } catch (error) {
          setErrorMessage(
            error?.response?.data
              ?.error ||
              error?.response?.data
                ?.message ||
              "Impossible de récupérer cette session.",
          );
        } finally {
          setDetailsLoading(
            false,
          );
        }
      },
      [],
    );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    refresh();
  }, [enabled, refresh]);

  return {
    rows,
    filters,

    page,
    limit,
    totalPages,
    count,

    selectedSession,

    loading,
    detailsLoading,
    errorMessage,

    setPage,
    updateFilter,
    resetFilters,

    refresh,
    selectSession,
  };
}